import React, { useState, useRef } from 'react';
import {
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 Button,
 IconButton,
 Typography,
 Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface DateRangeModalProps {
 open: boolean;
 handleClose: () => void;
}

export default function DateRangeModalCustom({
 open,
 handleClose,
}: DateRangeModalProps) {
 const [startDate, setStartDate] = useState<string>('');
 const [endDate, setEndDate] = useState<string>('');

 const hiddenStartRef = useRef<HTMLInputElement | null>(null);
 const hiddenEndRef = useRef<HTMLInputElement | null>(null);

 const formatDateToDisplay = (dateString: string) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}.${month}.${year}`;
 };

 const openCalendar = (
  ref: React.MutableRefObject<HTMLInputElement | null>,
 ) => {
  if (ref.current) {
   try {
    ref.current.showPicker();
   } catch (error) {
    ref.current.click();
   }
  }
 };

 const handleRequest = () => {
  console.log('Выбранный диапазон:', { startDate, endDate });
  handleClose();
 };

 return (
  <Dialog
   open={open}
   onClose={handleClose}
   maxWidth="sm"
   fullWidth
   PaperProps={{
    sx: {
     borderRadius: 3,
     p: 1,
     // 1. Черная рамка 2px вокруг всей формы
     border: '2px solid #000000',
     boxShadow: 'none',
    },
   }}
  >
   <DialogTitle
    sx={{
     m: 0,
     p: 2,
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'space-between',
    }}
   >
    <Typography
     variant="h6"
     sx={{ fontWeight: 600, color: '#333', marginLeft: '8px' }}
    >
     Выберите диапазон дат
    </Typography>
    <IconButton onClick={handleClose} sx={{ color: '#999' }}>
     <CloseIcon />
    </IconButton>
   </DialogTitle>

   <DialogContent
    sx={{
     borderTop: '1px solid #f0f0f0',
     borderBottom: '1px solid #f0f0f0',
     py: 4,
     px: 3,
    }}
   >
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
     {/* Скрытые инпуты */}
     <input
      type="date"
      ref={hiddenStartRef}
      value={startDate}
      onChange={(e) => setStartDate(e.target.value)}
      style={{
       position: 'absolute',
       width: 0,
       height: 0,
       opacity: 0,
       pointerEvents: 'none',
      }}
     />
     <input
      type="date"
      ref={hiddenEndRef}
      value={endDate}
      onChange={(e) => setEndDate(e.target.value)}
      style={{
       position: 'absolute',
       width: 0,
       height: 0,
       opacity: 0,
       pointerEvents: 'none',
      }}
     />

     {/* Поле выбора дат */}
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       // 2. Черная рамка 2px вокруг поля календаря
       border: '1px solid #000000',
       borderRadius: '50px',
       padding: '10px 24px',
       width: '100%',
       backgroundColor: '#fff',
       '&:focus-within': {
        borderColor: '#000000',
        boxShadow: '0 0 0 1px #000000',
       },
      }}
     >
      <input
       type="text"
       readOnly
       placeholder="Начальная дата"
       value={formatDateToDisplay(startDate)}
       onClick={() => openCalendar(hiddenStartRef)}
       onFocus={() => openCalendar(hiddenStartRef)}
       style={{
        flex: 1,
        fontSize: '15px',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        padding: 0,
        margin: 0,
        textAlign: 'left',
        width: '100%',
        cursor: 'pointer',
        color: startDate ? '#333' : '#aaa',
        fontFamily: 'Roboto, sans-serif',
       }}
      />

      <Typography sx={{ mx: 2, color: '#999', userSelect: 'none' }}>
       →
      </Typography>

      <input
       type="text"
       readOnly
       placeholder="Конечная дата"
       value={formatDateToDisplay(endDate)}
       onClick={() => openCalendar(hiddenEndRef)}
       onFocus={() => openCalendar(hiddenEndRef)}
       style={{
        flex: 1,
        fontSize: '15px',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        padding: 0,
        margin: 0,
        textAlign: 'left',
        width: '100%',
        cursor: 'pointer',
        color: endDate ? '#333' : '#aaa',
        fontFamily: 'Roboto, sans-serif',
       }}
      />

      <CalendarTodayIcon
       onClick={() =>
        !startDate ? openCalendar(hiddenStartRef) : openCalendar(hiddenEndRef)
       }
       sx={{ color: '#000000', fontSize: '20px', ml: 1, cursor: 'pointer' }}
      />
     </Box>
    </Box>
   </DialogContent>

   <DialogActions sx={{ p: 2, pr: 3 }}>
    <Button
     variant="contained"
     onClick={handleRequest}
     disabled={!startDate || !endDate}
     sx={{
      borderRadius: '50px',
      textTransform: 'none',
      backgroundColor: '#eee',
      color: '#555',
      fontWeight: 'medium',
      boxShadow: 'none',
      px: 4,
      py: 1.2,
      fontSize: '15px',
      '&:hover': {
       backgroundColor: '#e2e2e2',
       boxShadow: 'none',
      },
      '&:disabled': {
       backgroundColor: '#f5f5f5',
       color: '#bbb',
      },
     }}
    >
     Запросить журнал
    </Button>
   </DialogActions>
  </Dialog>
 );
}
